import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import ICO_PATCH_TEST from 'assets/images/icons/eye-dropper.svg';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import PERM from 'constants/core/perm.const';
import useAfn from 'hooks/useAfn.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import logger from 'utils/logger.util';

import beGetSettingsNcob from 'settings/actions/beGetSettingsNcob';
import bePutSettingsNcob from 'settings/actions/bePutSettingsNcob';
import { DropdownList } from 'settings/page-elements/dropdown/DropdownList';

import CN from './DropdownPatchTest.module.scss';
const L = logger('DropdownPatchTest');

export const DropdownPatchTest = ({ subName, isOffline, parentItems, childItemsPropName, childItemCheckPropName, onCheckboxClick }) => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: localLoading, nay: removeLoading, yay: addLoading } = useTip(false);
  const { val: isAllowOverlap, set: setOverlapping } = useTip(false);

  useEffect(() => {
    const fetchSettings = async () => {
      addLoading();
      const resultRead = await dispatch(beGetSettingsNcob());
      const json = resultRead.json ?? {};

      if (resultRead.is.ok) {
        setOverlapping(json.prevent_patch_test_from_overlapping_appointment);
      }
      removeLoading();
    };

    fetchSettings();
  }, [addLoading, dispatch, removeLoading, setOverlapping]);

  const [overlappingResult, handleChange] = useAfn(async () => {
    addLoading();
    const newValue = !isAllowOverlap;
    const result = await dispatch(
      bePutSettingsNcob({
        data: { prevent_patch_test_from_overlapping_appointment: newValue },
      }),
    );
    const json = result.json ?? {};
    if (!result.is.ok) {
      L.warn('handleChange()', 'result:', result);
      removeLoading();
      return;
    }

    setOverlapping(json.prevent_patch_test_from_overlapping_appointment);
    removeLoading();
  }, [addLoading, dispatch, isAllowOverlap, removeLoading, setOverlapping]);

  const isSpinnerRendered = overlappingResult.isLoading;

  if (overlappingResult.error) {
    L.warn('()', { overlappingResult });
  }

  const componentClasses = cn({
    [CN.component]: true,
    [CN.darkMode]: isDarkMode,
  });

  const offlineClasses = cn({
    [CN.dropdownList]: true,
    [CN.offline]: isOffline,
  });

  return (
    <Foldout tag="DropdownPatchTest" isOpen={false} title={t('onlineBooking.generalSettings.patchTestDropdown.foldoutTitle')} src={ICO_PATCH_TEST}>
      <div className={componentClasses} data-bem="DropdownPatchTest">
        {(isSpinnerRendered || localLoading) && <OverlaySpinner />}

        <div data-bem="DropdownPatchTest__checkbox" className={CN.checkboxContainer} role="button" onClick={handleChange} tabIndex={0}>
          <Icon variant={isAllowOverlap ? 'markSquare' : 'markSquareUnselected'} width="lg" />
          <span className={CN.checkboxLabel}>{t('onlineBooking.generalSettings.patchTestDropdown.checkboxLabel')}</span>
        </div>

        <div className={CN.text}>{subName}</div>
        <div className={offlineClasses} data-bem="DropdownPatchTest__DropdownList">
          {parentItems.map((item) => (
            <DropdownList
              listName={item.name}
              items={item[childItemsPropName]}
              childItemCheckPropName={childItemCheckPropName}
              onCheckboxClick={onCheckboxClick}
              key={item.id}
              permission={PERM.canEditServices}
              showSwitcher
              showDescription={false}
              variant="services"
            />
          ))}
        </div>
      </div>
    </Foldout>
  );
};
